import styled from '@emotion/styled';
import { IconButton, Link } from '@material-ui/core';
import { colors } from '@/styles';

const notStyleProp = (prop) =>
    !['tray', 'clear', 'over', 'dragging', 'service', 'claim', 'selected', 'warn', 'invalid'].includes(prop);

/** Board tints from the split modal design. The text shades are dark enough to read on the tray tints. */
const BOARD_COLORS = {
    columnBackground: '#f7f8fa',
    trayBackground: '#fffaf2',
    trayBorder: '#f0c98a',
    trayText: '#a0660d',
    clearBackground: '#f4faf5',
    clearBorder: '#b9dfbf',
    clearText: '#2e7d32',
    overBackground: '#eef5ff',
    grip: '#b9bdc6',
    service: '#6b5bd2',
    claim: '#00838f',
    switchBorder: '#c4c4c4',
    switchText: '#555',
    badgeOnSelected: 'rgba(255, 255, 255, 0.22)',
};

/** Below this width the board stacks: the tray on top, then each order full width. */
const PHONE_WIDTH = '600px';

/**
 * Row of columns: the tray first, then one column per new order. Scrolls sideways when the columns don't fit. At
 * phone width the columns stack instead, tray on top.
 */
export const BoardRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    overflow-x: auto;
    padding-bottom: 6px;
    @media (max-width: ${PHONE_WIDTH}) {
        flex-direction: column;
        align-items: stretch;
        overflow-x: visible;
    }
`;

/**
 * One column on the board. `tray` gives the Unassigned look, `clear` turns the tray green once it's empty,
 * `over` highlights it while a card is dragged over it, and `invalid` marks a column with a problem.
 */
export const Column = styled('section', { shouldForwardProp: notStyleProp })`
    flex: 0 0 ${({ tray }) => (tray ? '260px' : '270px')};
    display: flex;
    flex-direction: column;
    min-height: 240px;
    border-radius: 6px;
    border: 1px ${({ tray }) => (tray ? 'dashed' : 'solid')}
        ${({ tray, clear, over, invalid }) => {
            if (over) return colors.blues.primary;
            if (invalid) return colors.reds.primary;
            if (!tray) return colors.greys.border;
            return clear ? BOARD_COLORS.clearBorder : BOARD_COLORS.trayBorder;
        }};
    background: ${({ tray, clear, over }) => {
        if (over) return BOARD_COLORS.overBackground;
        if (!tray) return BOARD_COLORS.columnBackground;
        return clear ? BOARD_COLORS.clearBackground : BOARD_COLORS.trayBackground;
    }};
    @media (max-width: ${PHONE_WIDTH}) {
        flex: none;
        width: 100%;
        min-height: 120px;
    }
`;

/** Column header: name row, then a muted meta line. */
export const ColumnHeader = styled.header`
    padding: 10px 12px;
    border-bottom: 1px solid ${colors.greys.border};
`;

/** Name row in a column header, with room on the right for column controls. */
export const ColumnTitleRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
`;

/** Column name, for example "Unassigned" or "OD104409-1". */
export const ColumnName = styled.h4`
    margin: 0;
    font-size: 15px;
    font-weight: 500;
`;

/** Muted count line under a column name. */
export const ColumnMeta = styled.div`
    margin-top: 2px;
    color: ${colors.greys.primary};
    font-size: 12px;
`;

/** Stack of cards inside a column. */
export const Cards = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px;
`;

/** Dashed drop hint shown in a column with no cards. */
export const DropHint = styled.div`
    padding: 24px 8px;
    border: 2px dashed ${colors.greys.border};
    border-radius: 4px;
    color: ${colors.greys.primary};
    font-size: 13px;
    text-align: center;
`;

/** Tray footer message: orange while cards remain, green once all are placed. */
export const TrayMessage = styled('div', { shouldForwardProp: notStyleProp })`
    padding: 0 12px 10px;
    font-size: 12px;
    color: ${({ clear }) => (clear ? BOARD_COLORS.clearText : BOARD_COLORS.trayText)};
`;

/**
 * One card on the board: details on the left, quantity or amount on the right. `service` and `claim` add the service
 * or claim card's accent edge.
 */
export const Card = styled('article', { shouldForwardProp: notStyleProp })`
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr) auto;
    gap: 8px;
    align-items: start;
    padding: 8px 10px;
    background: ${colors.white.primary};
    border: 1px solid ${colors.greys.border};
    border-left: ${({ service, claim }) => {
        if (service) return `3px solid ${BOARD_COLORS.service}`;
        if (claim) return `3px solid ${BOARD_COLORS.claim}`;
        return `1px solid ${colors.greys.border}`;
    }};
    border-radius: 4px;
`;

/** Tag colour for service notes such as "Needs confirmation", used with `CardTag`. */
export const SERVICE_TAG_COLOR = BOARD_COLORS.service;

/** Tag colour for a claim card's status, used with `CardTag`. */
export const CLAIM_TAG_COLOR = BOARD_COLORS.claim;

/** Collapsed one-line summary of a column's item lines, shown above its services or claims in those modes. */
export const ItemSummary = styled.div`
    padding: 6px 10px;
    background: ${colors.white.primary};
    border: 1px solid ${colors.greys.border};
    border-radius: 4px;
    color: ${colors.greys.primary};
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/** Bold count part of the item summary, for example "3 lines · qty 8". */
export const ItemSummaryCount = styled.span`
    color: ${colors.onwardNavy};
    font-weight: 500;
`;

/** Segmented Items | Services | Claims switch above the board. */
export const ModeGroup = styled.div`
    display: inline-flex;
    margin-bottom: 12px;
    border: 1px solid ${BOARD_COLORS.switchBorder};
    border-radius: 4px;
    overflow: hidden;
`;

/** One option of the mode switch. `selected` marks the mode on show. */
export const ModeOption = styled('button', { shouldForwardProp: notStyleProp })`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    border: none;
    background: ${({ selected }) => (selected ? colors.onwardNavy : colors.white.primary)};
    color: ${({ selected }) => (selected ? colors.white.primary : BOARD_COLORS.switchText)};
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    & + & {
        border-left: 1px solid ${BOARD_COLORS.switchBorder};
    }
    &:focus-visible {
        outline: 2px solid ${colors.blues.primary};
        outline-offset: -2px;
    }
`;

/** Count badge on a mode option. `warn` turns it orange; `selected` lightens it on the dark selected option. */
export const ModeBadge = styled('span', { shouldForwardProp: notStyleProp })`
    padding: 0 7px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 500;
    line-height: 18px;
    background: ${({ warn, selected }) => {
        if (warn) return colors.oranges.primary;
        return selected ? BOARD_COLORS.badgeOnSelected : colors.greys.border;
    }};
    color: ${({ warn, selected }) => (warn || selected ? colors.white.primary : BOARD_COLORS.switchText)};
`;

/** Drag grip at the left of a card. Decorative; the whole card is the drag handle. */
export const CardGrip = styled.span`
    color: ${BOARD_COLORS.grip};
    font-size: 14px;
    line-height: 18px;
    user-select: none;
`;

/** Compact text-link button on a card, such as Move to. */
export const CardLinkButton = styled(Link)`
    display: inline-block;
    margin-top: 4px;
    padding: 0;
    min-width: 0;
    font-size: 12px;
    text-transform: none;
    &:disabled {
        color: ${colors.greys.primary};
        cursor: default;
        text-decoration: none;
    }
`;

/** Middle of a card. `min-width: 0` lets long text truncate instead of widening the card. */
export const CardBody = styled.div`
    min-width: 0;
`;

/** "Part of line N" note on a card that holds part of a divided line. */
export const CardPart = styled.div`
    font-size: 11px;
    color: ${colors.blues.primary};
`;

/** Card title: line number and SKU. */
export const CardTitle = styled.div`
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/** Card description, cut to one line. */
export const CardDescription = styled.div`
    color: ${colors.greys.secondary};
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

/** Tag row under a card's description. */
export const CardTags = styled.div`
    margin-top: 2px;
    font-size: 12px;
`;

/** Coloured tag text. `tagColor` is the tag's colour. */
export const CardTag = styled('span', { shouldForwardProp: (prop) => prop !== 'tagColor' })`
    color: ${({ tagColor }) => tagColor};
`;

/** Card quantity, right-aligned. */
export const CardQuantity = styled.div`
    font-weight: 500;
    text-align: right;
`;

/** Right edge of a card: the quantity (items only) above the info button. */
export const CardAside = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
`;

/** Info button that opens a card's details dialog. */
export const CardInfoButton = styled(IconButton)`
    padding: 2px;
    color: ${colors.greys.primary};
    &:hover {
        color: ${colors.onwardNavy};
    }
`;

/** Hover target for a card's full-name tooltip: the title and description lines. */
export const CardNames = styled.div`
    min-width: 0;
`;

/** Label and value grid in a card's details dialog. */
export const DetailList = styled.dl`
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: 6px 16px;
    margin: 0;
    & dt {
        color: ${colors.greys.primary};
        font-size: 13px;
    }
    & dd {
        margin: 0;
        overflow-wrap: anywhere;
    }
`;

/** Wrapper that makes a card draggable. `dragging` lifts it while it moves. */
export const DragItem = styled('div', { shouldForwardProp: notStyleProp })`
    border-radius: 4px;
    cursor: grab;
    box-shadow: ${({ dragging }) => (dragging ? '0 4px 14px rgba(0, 0, 0, 0.18)' : 'none')};
    &:focus-visible {
        outline: 2px solid ${colors.blues.primary};
        outline-offset: 2px;
    }
`;

/** "+ Add order" button at the end of the board, sized like a column. */
export const AddOrderButton = styled.button`
    flex: 0 0 120px;
    min-height: 240px;
    @media (max-width: ${PHONE_WIDTH}) {
        flex: none;
        width: 100%;
        min-height: 48px;
    }
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed ${colors.greys.border};
    border-radius: 6px;
    background: ${colors.white.primary};
    color: ${colors.blues.primary};
    font: inherit;
    font-weight: 500;
    cursor: pointer;
    &:hover:not(:disabled) {
        background: ${BOARD_COLORS.overBackground};
    }
    &:focus-visible {
        outline: 2px solid ${colors.blues.primary};
        outline-offset: 2px;
    }
    &:disabled {
        color: ${colors.greys.primary};
        cursor: default;
    }
`;

/** "Billable" text and switch in a column header. */
export const BillableLabel = styled.label`
    display: inline-flex;
    align-items: center;
    margin: 0;
    color: ${colors.greys.primary};
    font-size: 12px;
    cursor: pointer;
`;

/** Problem message at the bottom of a new-order column, such as "Add at least one item". */
export const ColumnError = styled.div`
    padding: 0 12px 10px;
    color: ${colors.reds.primary};
    font-size: 12px;
`;

/** Note under a column header whose Billable switch is off. */
export const NotBillableNote = styled.div`
    margin-top: 4px;
    color: ${colors.oranges.primary};
    font-size: 12px;
`;

/** Controls on the right of a column header, in one row. */
export const ColumnActions = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

/** A line of context in the "How many to move?" prompt. */
export const PromptText = styled.p`
    margin: 0 0 8px;
    font-size: 14px;
    color: ${colors.greys[3]};
`;

/** The − / quantity / + row in the "How many to move?" prompt. */
export const PromptStepper = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    .MuiIconButton-root {
        margin-top: 4px;
    }
`;
